// #/horizon/handshake.ts
import  {type HorizonCaps} from '#/modules/caps'

let _inFlight: Promise<HorizonCaps> | null = null
let _lastOk: {at: number; caps: HorizonCaps} | null = null

export async function fetchSessionCaps(
  accessJwt?: string | null,
): Promise<HorizonCaps> {
  // tiny cache to avoid StrictMode/double-effect spam
  if (_lastOk && Date.now() - _lastOk.at < 1500) {
    console.log('[horizon] fetchSessionCaps cache-hit')
    return _lastOk.caps
  }

  if (_inFlight) {
    console.log('[horizon] fetchSessionCaps dedupe: awaiting in-flight')
    return _inFlight
  }

  _inFlight = (async () => {
    const VERSION = '2026-01-17g'
    const url = 'https://api.pursuingpeace.app/xrpc/hideaway.getSessionCaps'

    console.log('[horizon] fetchSessionCaps VERSION =', VERSION)
    console.log('[horizon] fetchSessionCaps ->', url)

    // Fail closed when not logged in
    if (!accessJwt) {
      console.log('[horizon] fetchSessionCaps: no accessJwt; returning empty caps')
      const caps = {} as HorizonCaps
      _lastOk = {at: Date.now(), caps}
      return caps
    }

    const startedAt = Date.now()
    const ac = new AbortController()

    // Cancellable watchdog (prevents “late throw” after success)
    let watchdogTimer: any = null
    const watchdog = new Promise<never>((_, reject) => {
      watchdogTimer = setTimeout(() => {
        console.log('[horizon] WATCHDOG: still waiting after ~4.1s', {
          elapsedMs: Date.now() - startedAt,
          url,
        })
        try {
          ac.abort()
        } catch {}
        reject(new Error('caps handshake timeout (watchdog)'))
      }, 4100)
    })

    const req = fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${accessJwt}`,
      },
      // We use Authorization; cookies are not required.
      credentials: 'omit',
      signal: ac.signal,
    })
      .then(res => {
        console.log('[horizon] fetch settled: RESOLVED', {
          elapsedMs: Date.now() - startedAt,
          status: res.status,
          ok: res.ok,
        })
        return res
      })
      .catch(err => {
        console.log('[horizon] fetch settled: REJECTED', {
          elapsedMs: Date.now() - startedAt,
          name: (err as any)?.name,
          message: (err as any)?.message,
        })
        throw err
      })
      .finally(() => {
        if (watchdogTimer) clearTimeout(watchdogTimer)
      })

    // whichever happens first will now always produce a log
    const res = await Promise.race([req, watchdog])

    if (!res.ok) {
      let body = ''
      try {
        body = await res.text()
      } catch {}
      throw new Error(
        `caps handshake failed: status=${res.status} body=${body.slice(0, 200)}`,
      )
    }

    const json = (await res.json()) as any
    console.log('[horizon] caps payload', json)

    const caps = (json?.caps ?? {}) as HorizonCaps
    _lastOk = {at: Date.now(), caps}
    return caps
  })().finally(() => {
    _inFlight = null
  })

  return _inFlight
}
